import { useState } from "react";
import {
  anchorName,
  parseDay,
  type Channel,
  type KeyDateRef,
  type SequenceGroup,
  type TriggerDirection,
} from "./emailSmsTypes";

export type AddStepDraft = {
  ch: Channel;
  days: string;
  dir: TriggerDirection;
  anchor: string;
  grp: string;
};

type Props = {
  groups: SequenceGroup[];
  keyDates: KeyDateRef[];
  /** Whether each channel is live on both the campaign and this sequence. */
  emailLive: boolean;
  smsLive: boolean;
  campaignStart?: string | null;
  campaignEnd?: string | null;
  onCancel: () => void;
  onSubmit: (draft: AddStepDraft) => void;
};

// SMS lands an hour after email by convention, so a same-day pair arrives in a sensible order.
const SEND_HOUR: Record<Channel, number> = { email: 10, sms: 11 };

const hourLabel = (hour: number) =>
  hour === 0 || hour === 24 ? "midnight" : hour === 12 ? "noon" : hour < 12 ? `${hour}am` : `${hour - 12}pm`;

const longDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/*
    A step added to one campaign's copy of a sequence, not to the catalog. The trigger is set
    here and the copy is written next: the form ends in "Add and write copy" rather than
    "Add", because a touch with no copy is a scheduled blank.
*/
export function AddStepForm({
  groups,
  keyDates,
  emailLive,
  smsLive,
  campaignStart,
  campaignEnd,
  onCancel,
  onSubmit,
}: Props) {
  const [draft, setDraft] = useState<AddStepDraft>({
    ch: emailLive || !smsLive ? "email" : "sms",
    days: "7",
    dir: "before",
    anchor: keyDates[0]?.type ?? "app",
    grp: groups[0]?.id ?? "main",
  });

  const set = (patch: Partial<AddStepDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const channelLive = draft.ch === "email" ? emailLive : smsLive;
  const anchorDate = keyDates.find((k) => k.type === draft.anchor)?.date;

  const sendDate = (() => {
    if (!anchorDate) return null;
    const base = parseDay(anchorDate);
    if (!base) return null;
    const days = draft.dir === "on" ? 0 : parseInt(draft.days, 10) || 0;
    base.setDate(base.getDate() + (draft.dir === "before" ? -days : days));
    return base;
  })();

  const start = parseDay(campaignStart);
  const end = parseDay(campaignEnd);
  const outside =
    !!sendDate &&
    ((start && !isNaN(start.getTime()) && sendDate < start) ||
      (end && !isNaN(end.getTime()) && sendDate > end));

  const hour = SEND_HOUR[draft.ch];
  const sendLabel = !sendDate
    ? `! No send date. ${anchorName(draft.anchor)} isn't on this campaign.`
    : `${outside ? "! Sends " : "Sends "}${longDate(sendDate)}, ${hourLabel(hour)}${outside ? ", outside the campaign dates" : ""}`;

  const anchorOptions = keyDates.map((k) => ({
    v: k.type,
    label: k.label?.trim() || anchorName(k.type),
  }));

  return (
    <div className="add-step" role="group" aria-label="Add a step">
      <div className="add-step__head">
        <span className="add-step__title">Add a step</span>
        <span className="add-step__note">
          Applies to this campaign only. You write the copy next.
        </span>
      </div>

      <div className="add-step__row">
        <span className="add-step__label">Channel</span>
        <div className="btn-group btn-group-sm" role="group" aria-label="Channel">
          {(["email", "sms"] as Channel[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn btn-sm ${draft.ch === option ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => set({ ch: option })}
            >
              {option === "email" ? "Email" : "SMS"}
            </button>
          ))}
        </div>
        {!channelLive && (
          <span className="add-step__warn">
            ! {draft.ch === "email" ? "Email" : "SMS"} is off for this playbook. Turn it on above
            first.
          </span>
        )}
      </div>

      <div className="add-step__row">
        <span className="add-step__label">When</span>
        {draft.dir !== "on" && (
          <>
            <input
              type="number"
              min={0}
              className="add-step__days"
              value={draft.days}
              aria-label="Days"
              onChange={(e) => set({ days: e.target.value.replace(/[^0-9]/g, "") })}
            />
            <span className="add-step__unit">days</span>
          </>
        )}
        <select
          className="add-step__select"
          value={draft.dir}
          aria-label="Before, after or on"
          onChange={(e) => set({ dir: e.target.value as TriggerDirection })}
        >
          <option value="before">before</option>
          <option value="after">after</option>
          <option value="on">on</option>
        </select>
        <select
          className="add-step__select add-step__select--anchor"
          value={draft.anchor}
          aria-label="Key date"
          onChange={(e) => set({ anchor: e.target.value })}
        >
          {anchorOptions.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {groups.length > 1 && (
        <div className="add-step__row">
          <label className="add-step__label" htmlFor="add-step-grp">
            Group
          </label>
          <select
            id="add-step-grp"
            className="add-step__select add-step__select--anchor"
            value={draft.grp}
            onChange={(e) => set({ grp: e.target.value })}
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name || "Main sequence"}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="add-step__foot">
        <span className={`add-step__send${!sendDate || outside ? " add-step__send--warn" : ""}`}>
          {sendLabel}
        </span>
        <span className="esms__spacer" />
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!sendDate || !channelLive}
          onClick={() => onSubmit(draft)}
        >
          Add and write copy
        </button>
      </div>
    </div>
  );
}

export default AddStepForm;
